import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const nodeDetails = {
  sourceSystem: {
    title: "Source system",
    type: "System of record",
    description:
      "The operational application that owns the original data. This is where traceability should eventually land when a dashboard number is challenged.",
    evidence: ["Core Banking API", "Endpoint: /accounts/balances", "Business owner: Lending Operations"],
  },
  sourceDataset: {
    title: "ADF source dataset",
    type: "ADF dataset",
    description:
      "The source dataset referenced by the copy activity. In a real lineage tool this is parsed from the ADF pipeline JSON and linked service definition.",
    evidence: ["Dataset: ds_core_account_balance_api", "Linked service: ls_core_banking_api", "Relative URL: /accounts/balances"],
  },
  pipelineJson: {
    title: "Pipeline JSON in GitHub",
    type: "Source-controlled config",
    description:
      "The pipeline definition is read from source control so the documentation follows implemented code instead of a manually maintained flow chart.",
    evidence: ["factory/pipeline/pl_ingest_core_account.json", "Source dataset reference", "Sink dataset reference", "Copy activity policy"],
  },
  adfCopy: {
    title: "ADF copy activity",
    type: "Ingestion activity",
    description:
      "The activity connects the source dataset to the sink dataset. This is the bridge between an external source and the bronze landing table.",
    evidence: ["Activity: Copy_CoreAccount_To_Bronze", "Type: Copy", "Source: RestSource", "Sink: AzureSqlSink"],
  },
  sinkDataset: {
    title: "ADF sink dataset",
    type: "ADF dataset",
    description:
      "The sink dataset declares where ADF lands data. This should resolve to a concrete bronze or staging asset.",
    evidence: ["Dataset: ds_bronze_core_account_balance", "Schema: bronze", "Table: core_account_balance"],
  },
  bronzeTable: {
    title: "Bronze ingestion table",
    type: "Landing layer",
    description:
      "Raw or lightly typed rows landed by ADF, with load metadata preserved for replay, investigation and audit evidence.",
    evidence: ["bronze.core_account_balance", "Columns: account_id, snapshot_date, balance_amount, status_code", "Audit fields: load_id, source_file, ingested_at"],
  },
  silverProc: {
    title: "Silver cleansing procedure",
    type: "SQL transformation",
    description:
      "Reads from bronze, standardises data types, removes invalid records and maps source-system codes into business statuses.",
    evidence: ["Procedure: silver.usp_CleanseLoanBalance", "Source table: bronze.core_account_balance", "Target table: silver.loan_balance"],
    sql: `CREATE OR ALTER PROCEDURE silver.usp_CleanseLoanBalance
AS
BEGIN
  INSERT INTO silver.loan_balance
  SELECT
    TRY_CONVERT(bigint, account_id) AS loan_id,
    TRY_CONVERT(date, snapshot_date) AS snapshot_date,
    TRY_CONVERT(decimal(18,2), balance_amount) AS current_balance,
    CASE
      WHEN status_code IN ('A', 'ACTIVE') THEN 'Active'
      WHEN status_code IN ('C', 'CLOSED') THEN 'Closed'
      ELSE 'Review'
    END AS account_status
  FROM bronze.core_account_balance
  WHERE account_id IS NOT NULL;
END;`,
  },
  silverTable: {
    title: "Silver conformed table",
    type: "Standardised layer",
    description:
      "Cleaned and conformed data with consistent keys, types, statuses and source-system differences resolved.",
    evidence: ["silver.loan_balance", "Conformed key: loan_id", "Validated fields: current_balance, account_status, snapshot_date"],
  },
  goldProc: {
    title: "Gold build procedure",
    type: "SQL transformation",
    description:
      "Transforms silver data into dimensional reporting structures and applies final business rules for the star schema.",
    evidence: ["Procedure: gold.usp_LoadGoldLoanBalance", "Source table: silver.loan_balance", "Targets: FactLoanBalance, DimLoan, DimDate"],
    sql: `CREATE OR ALTER PROCEDURE gold.usp_LoadGoldLoanBalance
AS
BEGIN
  INSERT INTO gold.FactLoanBalance
  SELECT
    l.loan_key,
    d.date_key,
    b.current_balance,
    b.accrued_interest,
    b.account_status
  FROM silver.loan_balance b
  JOIN gold.DimLoan l ON b.loan_id = l.loan_id
  JOIN gold.DimDate d ON b.snapshot_date = d.full_date;
END;`,
  },
  dimLoan: {
    title: "Gold dimension",
    type: "Star schema asset",
    description:
      "A conformed dimension used to describe loan attributes. The fact table joins to it through a surrogate key.",
    evidence: ["gold.DimLoan", "Business key: loan_id", "Surrogate key: loan_key"],
  },
  dimDate: {
    title: "Gold dimension",
    type: "Star schema asset",
    description:
      "A shared date dimension used by Power BI for time intelligence and consistent reporting periods.",
    evidence: ["gold.DimDate", "Business key: full_date", "Surrogate key: date_key"],
  },
  factBalance: {
    title: "Gold fact table",
    type: "Star schema asset",
    description:
      "The business-ready fact table at the loan and snapshot-date grain. This is the table the semantic model should use for balance measures.",
    evidence: ["gold.FactLoanBalance", "Grain: loan_key + date_key", "Measures: current_balance, accrued_interest"],
  },
  modelFact: {
    title: "Semantic model table",
    type: "Power BI model asset",
    description:
      "The imported or DirectQuery table inside the Power BI semantic model that points to the curated gold fact table.",
    evidence: ["Model table: Fact Loan Balance", "Source: gold.FactLoanBalance", "Refresh policy or DirectQuery source"],
  },
  modelDims: {
    title: "Semantic model relationships",
    type: "Power BI model asset",
    description:
      "Relationships and dimensions provide filter context. They are part of lineage because the same measure can change meaning when relationship logic changes.",
    evidence: ["Fact Loan Balance -> Dim Loan", "Fact Loan Balance -> Dim Date", "Cross-filter direction and cardinality"],
  },
  daxMeasure: {
    title: "DAX measure",
    type: "Metric logic",
    description:
      "The metric definition used by the visual. This is often the first real technical dependency behind a dashboard number.",
    evidence: ["Measure: [Total Active Balance]", "Expression: SUM('Fact Loan Balance'[current_balance])", "Filter: account_status = Active"],
    sql: `Total Active Balance =
CALCULATE(
  SUM('Fact Loan Balance'[current_balance]),
  'Fact Loan Balance'[account_status] = "Active"
)`,
  },
  reportVisual: {
    title: "Power BI visual",
    type: "Report dependency",
    description:
      "The specific visual that displays the number. This links the business question to a concrete measure and filter context.",
    evidence: ["Report: Executive Lending Overview", "Page: Portfolio Summary", "Visual: Total Active Balance card"],
  },
  reportPage: {
    title: "Power BI report page",
    type: "Consumption layer",
    description:
      "The report page groups visuals, slicers and page-level filters. It is the visible business surface of the lineage chain.",
    evidence: ["Workspace: Finance Analytics", "Report: Executive Lending Overview", "Page filters: Region, Month, Product"],
  },
};

const rawNodes = [
  ["sourceSystem", 0, 300, "Source system", "Core Banking API", "source", "source"],
  ["sourceDataset", 330, 300, "Source dataset", "ds_core_account_balance_api", "pipeline", "adf dataset"],
  ["pipelineJson", 330, 60, "Pipeline JSON", "pl_ingest_core_account.json", "config", "github"],
  ["adfCopy", 660, 180, "Copy activity", "Copy_CoreAccount_To_Bronze", "pipeline", "adf activity"],
  ["sinkDataset", 990, 180, "Sink dataset", "ds_bronze_core_account_balance", "pipeline", "adf dataset"],
  ["bronzeTable", 1320, 180, "Bronze table", "bronze.core_account_balance", "bronze", "bronze"],
  ["silverProc", 1650, 60, "Silver proc", "silver.usp_CleanseLoanBalance", "sql", "sql"],
  ["silverTable", 1650, 330, "Silver table", "silver.loan_balance", "silver", "silver"],
  ["goldProc", 1980, 60, "Gold proc", "gold.usp_LoadGoldLoanBalance", "sql", "sql"],
  ["dimLoan", 2310, 0, "DimLoan", "gold.DimLoan", "gold", "gold dimension"],
  ["dimDate", 2310, 160, "DimDate", "gold.DimDate", "gold", "gold dimension"],
  ["factBalance", 2310, 340, "FactLoanBalance", "gold.FactLoanBalance", "gold", "gold fact"],
  ["modelFact", 2640, 340, "Model table", "Fact Loan Balance", "semantic", "semantic"],
  ["modelDims", 2640, 120, "Relationships", "Fact -> DimLoan, DimDate", "semantic", "semantic"],
  ["daxMeasure", 2970, 230, "DAX measure", "[Total Active Balance]", "semantic", "measure"],
  ["reportVisual", 3300, 230, "Report visual", "Total Active Balance card", "report", "visual"],
  ["reportPage", 3630, 230, "Report page", "Portfolio Summary", "report", "report"],
];

const GRAPH_VIEWPORT = { x: 48, y: 180, zoom: 0.62 };
const rawEdges = [
  ["source-sourceDataset", "sourceSystem", "sourceDataset", "read endpoint"],
  ["json-sourceDataset", "pipelineJson", "sourceDataset", "declares source"],
  ["json-adfCopy", "pipelineJson", "adfCopy", "defines activity"],
  ["sourceDataset-adfCopy", "sourceDataset", "adfCopy", "source input"],
  ["adfCopy-sinkDataset", "adfCopy", "sinkDataset", "writes to sink"],
  ["json-sinkDataset", "pipelineJson", "sinkDataset", "declares sink"],
  ["sinkDataset-bronzeTable", "sinkDataset", "bronzeTable", "resolves table"],
  ["bronzeTable-silverProc", "bronzeTable", "silverProc", "FROM bronze"],
  ["silverProc-silverTable", "silverProc", "silverTable", "INSERT silver"],
  ["silverTable-goldProc", "silverTable", "goldProc", "FROM silver"],
  ["goldProc-dimLoan", "goldProc", "dimLoan", "lookup/join"],
  ["goldProc-dimDate", "goldProc", "dimDate", "lookup/join"],
  ["goldProc-factBalance", "goldProc", "factBalance", "INSERT fact"],
  ["dimLoan-modelDims", "dimLoan", "modelDims", "relationship"],
  ["dimDate-modelDims", "dimDate", "modelDims", "relationship"],
  ["factBalance-modelFact", "factBalance", "modelFact", "model source"],
  ["modelFact-modelDims", "modelFact", "modelDims", "joins"],
  ["modelFact-daxMeasure", "modelFact", "daxMeasure", "SUM column"],
  ["modelDims-daxMeasure", "modelDims", "daxMeasure", "filter context"],
  ["daxMeasure-reportVisual", "daxMeasure", "reportVisual", "used by visual"],
  ["reportVisual-reportPage", "reportVisual", "reportPage", "placed on page"],
];

function buildMeasuredPath(sourceBox, targetBox) {
  const sourceCenter = {
    x: sourceBox.left + sourceBox.width / 2,
    y: sourceBox.top + sourceBox.height / 2,
  };
  const targetCenter = {
    x: targetBox.left + targetBox.width / 2,
    y: targetBox.top + targetBox.height / 2,
  };

  const dx = targetCenter.x - sourceCenter.x;
  const dy = targetCenter.y - sourceCenter.y;
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  const gap = 12;

  let start;
  let end;

  if (horizontal) {
    start =
      dx >= 0
        ? { x: sourceBox.right + gap, y: sourceCenter.y }
        : { x: sourceBox.left - gap, y: sourceCenter.y };
    end =
      dx >= 0
        ? { x: targetBox.left - gap, y: targetCenter.y }
        : { x: targetBox.right + gap, y: targetCenter.y };

    const curve = Math.max(48, Math.abs(end.x - start.x) * 0.42);
    const c1x = start.x + (dx >= 0 ? curve : -curve);
    const c2x = end.x - (dx >= 0 ? curve : -curve);

    return {
      path: `M ${start.x} ${start.y} C ${c1x} ${start.y}, ${c2x} ${end.y}, ${end.x} ${end.y}`,
      labelX: (start.x + end.x) / 2,
      labelY: (start.y + end.y) / 2 - 12,
    };
  }

  start =
    dy >= 0
      ? { x: sourceCenter.x, y: sourceBox.bottom + gap }
      : { x: sourceCenter.x, y: sourceBox.top - gap };
  end =
    dy >= 0
      ? { x: targetCenter.x, y: targetBox.top - gap }
      : { x: targetCenter.x, y: targetBox.bottom + gap };

  const curve = Math.max(42, Math.abs(end.y - start.y) * 0.42);
  const c1y = start.y + (dy >= 0 ? curve : -curve);
  const c2y = end.y - (dy >= 0 ? curve : -curve);

  return {
    path: `M ${start.x} ${start.y} C ${start.x} ${c1y}, ${end.x} ${c2y}, ${end.x} ${end.y}`,
    labelX: (start.x + end.x) / 2,
    labelY: (start.y + end.y) / 2 - 12,
  };
}

function traceLineage(selectedId) {
  const upstream = new Set([selectedId]);
  const downstream = new Set([selectedId]);
  const activeEdges = new Set();
  let changed = true;

  while (changed) {
    changed = false;

    for (const [edgeId, source, target] of rawEdges) {
      if (upstream.has(target) && !upstream.has(source)) {
        upstream.add(source);
        activeEdges.add(edgeId);
        changed = true;
      }

      if (downstream.has(source) && !downstream.has(target)) {
        downstream.add(target);
        activeEdges.add(edgeId);
        changed = true;
      }
    }
  }

  for (const [edgeId, source, target] of rawEdges) {
    if (
      (upstream.has(source) && upstream.has(target)) ||
      (downstream.has(source) && downstream.has(target))
    ) {
      activeEdges.add(edgeId);
    }
  }

  return {
    activeEdges,
    relatedNodes: new Set([...upstream, ...downstream]),
    upstreamCount: upstream.size - 1,
    downstreamCount: downstream.size - 1,
  };
}

function LineageNode({ data }) {
  return (
    <button
      type="button"
      className={`flow-lineage-node ${data.layer} ${data.isSelected ? "selected" : ""} ${
        data.isRelated ? "related" : "faded"
      }`}
    >
      <Handle type="target" position={Position.Left} />
      <span>{data.layerLabel}</span>
      <strong>{data.label}</strong>
      <em>{data.asset}</em>
      <Handle type="source" position={Position.Right} />
    </button>
  );
}

const nodeTypes = { lineageNode: LineageNode };

const nodeClassName = (node) => node.data?.layer || "";

export default function LineageFlowExplorer() {
  const [selectedId, setSelectedId] = useState("reportVisual");
  const [viewport, setViewport] = useState(GRAPH_VIEWPORT);
  const [nodeBoxes, setNodeBoxes] = useState({});
  const canvasRef = useRef(null);
  const selected = nodeDetails[selectedId];
  const trace = useMemo(() => traceLineage(selectedId), [selectedId]);

  const nodes = useMemo(
    () =>
      rawNodes.map(([id, x, y, label, asset, layer, layerLabel]) => ({
        id,
        type: "lineageNode",
        position: { x, y },
        data: {
          label,
          asset,
          layer,
          layerLabel,
          isSelected: selectedId === id,
          isRelated: trace.relatedNodes.has(id),
        },
      })),
    [selectedId, trace.relatedNodes]
  );

  useEffect(() => {
    const measure = () => {
      if (!canvasRef.current) {
        return;
      }

      const canvasBox = canvasRef.current.getBoundingClientRect();
      const nextBoxes = {};

      rawNodes.forEach(([id]) => {
        const element = canvasRef.current.querySelector(`.react-flow__node[data-id="${id}"]`);

        if (!element) {
          return;
        }

        const box = element.getBoundingClientRect();
        nextBoxes[id] = {
          left: box.left - canvasBox.left,
          right: box.right - canvasBox.left,
          top: box.top - canvasBox.top,
          bottom: box.bottom - canvasBox.top,
          width: box.width,
          height: box.height,
        };
      });

      setNodeBoxes(nextBoxes);
    };

    measure();
    const frame = window.requestAnimationFrame(measure);
    const timer = window.setTimeout(measure, 180);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [viewport, selectedId]);

  const edges = useMemo(
    () =>
      rawEdges.map(([id, source, target, label]) => ({
        id,
        source,
        target,
        label,
        hidden: true,
      })),
    []
  );

  const visibleEdges = useMemo(
    () =>
      rawEdges
        .map(([id, sourceId, targetId, label]) => {
          const sourceBox = nodeBoxes[sourceId];
          const targetBox = nodeBoxes[targetId];

          if (!sourceBox || !targetBox) {
            return null;
          }

          const route = buildMeasuredPath(sourceBox, targetBox);

          return {
            id,
            label,
            active: trace.activeEdges.has(id),
            ...route,
          };
        })
        .filter(Boolean),
    [nodeBoxes, trace.activeEdges]
  );

  return (
    <figure className="react-flow-lineage-figure">
      <div className="react-flow-copy">
        <span>Interactive dependency map</span>
        <h3>Object-level lineage, not just layer-level boxes</h3>
        <p>
          Click a node to inspect the evidence a lineage tool should capture. The graph
          links individual report, model, SQL, table and ADF assets so impact analysis can
          answer what depends on what.
        </p>
      </div>

      <div className="react-flow-shell">
        <div className="react-flow-canvas" ref={canvasRef} aria-label="Object-level Power BI to source lineage flow">
          <svg className="lineage-visible-edge-layer" aria-hidden="true">
            <defs>
              <marker
                id="lineage-visible-arrow"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="3"
                markerHeight="3"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" />
              </marker>
            </defs>
            {visibleEdges.map((edge) => (
              <g key={edge.id} className={edge.active ? "active" : "muted"}>
                <path d={edge.path} />
                <text x={edge.labelX} y={edge.labelY}>
                  {edge.label}
                </text>
              </g>
            ))}
          </svg>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            defaultViewport={GRAPH_VIEWPORT}
            onMove={(_, nextViewport) => setViewport(nextViewport)}
            minZoom={0.22}
            maxZoom={1.15}
            nodesDraggable
            nodesConnectable={false}
            elementsSelectable
            panOnDrag
            panOnScroll
            zoomOnScroll
            zoomOnPinch
            zoomOnDoubleClick={false}
            onNodeClick={(_, node) => setSelectedId(node.id)}
          >
            <MiniMap zoomable pannable nodeClassName={nodeClassName} />
            <Controls showInteractive={false} />
            <Background color="#d0d5dd" gap={18} />
          </ReactFlow>
        </div>

        <aside className="react-flow-detail-panel">
          <span>{selected.type}</span>
          <h3>{selected.title}</h3>
          <p>{selected.description}</p>
          <div className="lineage-trace-summary">
            <strong>{trace.upstreamCount}</strong>
            <span>upstream</span>
            <strong>{trace.downstreamCount}</strong>
            <span>downstream</span>
          </div>
          <h4>Evidence to capture</h4>
          <ul>
            {selected.evidence.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          {selected.sql && (
            <>
              <h4>Implementation logic</h4>
              <pre>{selected.sql}</pre>
            </>
          )}
        </aside>
      </div>

      <figcaption>
        Example object-level lineage: ADF JSON defines source and sink datasets, SQL
        procedures link bronze, silver and gold assets, and the semantic model connects
        fact and dimension tables to the Power BI visual that displays the metric.
      </figcaption>
    </figure>
  );
}
